import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, MessageCircle, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PageContainer, PageHeader, Card, Pill } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const LOG_STATUS = {
  sent: "green",
  failed: "red",
  skipped: "slate",
  queued: "amber",
};

export default function WhatsAppSettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const branchName = user?.default_branch?.name || "selected branch";

  const [form, setForm] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [reservation, setReservation] = useState({
    recipient_phone: "",
    guest_name: "",
    reservation_date: "",
    reservation_time: "",
    party_size: "2",
    table_ref: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["whatsapp-settings", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/whatsapp/settings", {
        params: branchId ? { branch_id: branchId } : {},
      });
      return res.settings;
    },
    enabled: Boolean(user),
  });

  useEffect(() => {
    if (data && !form) {
      setForm({ ...data, access_token: "" });
    }
  }, [data, form]);

  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ["whatsapp-logs", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/whatsapp/logs", {
        params: branchId ? { branch_id: branchId } : {},
      });
      return res.logs ?? [];
    },
    enabled: Boolean(user),
  });

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const { data: res } = await api.put("/whatsapp/settings", {
        branch_id: branchId,
        ...payload,
      });
      return res;
    },
    onSuccess: (res) => {
      toast.success(res.message || "Settings saved.");
      setForm({ ...res.settings, access_token: "" });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-settings", branchId] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not save settings.");
    },
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const { data: res } = await api.post("/whatsapp/test", {
        branch_id: branchId,
        recipient_phone: testPhone,
      });
      return res;
    },
    onSuccess: (res) => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: ["whatsapp-logs", branchId] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Test failed.");
    },
  });

  const reservationMutation = useMutation({
    mutationFn: async () => {
      const { data: res } = await api.post("/whatsapp/reservation", {
        branch_id: branchId,
        ...reservation,
        party_size: Number(reservation.party_size),
      });
      return res;
    },
    onSuccess: (res) => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: ["whatsapp-logs", branchId] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not send reservation.");
    },
  });

  function save() {
    if (!form) return;
    const payload = { ...form };
    if (!payload.access_token) delete payload.access_token;
    delete payload.has_access_token;
    delete payload.webhook_docs_url;
    delete payload.branch_id;
    saveMutation.mutate(payload);
  }

  if (isLoading || !form) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-muted-foreground py-12">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading WhatsApp settings…
        </div>
      </PageContainer>
    );
  }

  const logs = logsData ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t("whatsapp.title", "WhatsApp")}
        subtitle={t("whatsapp.subtitle", "Customer messages for {{branch}} — bills, order ready, delivery ETA.", {
          branch: branchName,
        })}
      />

      <div className="mb-4">
        <Pill tone={form.is_enabled ? "green" : "slate"}>
          {form.is_enabled ? "WhatsApp enabled" : "WhatsApp disabled"}
        </Pill>
      </div>

      <Tabs defaultValue="connection">
        <TabsList className="mb-4">
          <TabsTrigger value="connection">Connection</TabsTrigger>
          <TabsTrigger value="automation">Automation</TabsTrigger>
          <TabsTrigger value="reservation">Reservations</TabsTrigger>
          <TabsTrigger value="logs">Message log</TabsTrigger>
        </TabsList>

        <TabsContent value="connection">
          <Card className="p-4 space-y-4 max-w-2xl">
            <div className="flex items-center justify-between">
              <Label>Enable WhatsApp for this branch</Label>
              <Switch
                checked={form.is_enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_enabled: v }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Phone number ID (Meta Cloud API)</Label>
              <Input
                value={form.phone_number_id || ""}
                onChange={(e) => setForm((f) => ({ ...f, phone_number_id: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Business account ID</Label>
              <Input
                value={form.business_account_id || ""}
                onChange={(e) => setForm((f) => ({ ...f, business_account_id: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>
                Access token {form.has_access_token && "(leave blank to keep current)"}
              </Label>
              <Input
                type="password"
                value={form.access_token || ""}
                onChange={(e) => setForm((f) => ({ ...f, access_token: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>API version</Label>
                <Input
                  value={form.api_version || "v21.0"}
                  onChange={(e) => setForm((f) => ({ ...f, api_version: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label>Default country code</Label>
                <Input
                  value={form.default_country_code || "971"}
                  onChange={(e) => setForm((f) => ({ ...f, default_country_code: e.target.value }))}
                />
              </div>
            </div>
            <a
              href="https://developers.facebook.com/docs/whatsapp/cloud-api"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary"
            >
              Meta WhatsApp Cloud API docs <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <div className="flex gap-2 pt-2">
              <Button onClick={save} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
            <div className="border-t pt-4 grid gap-2">
              <Label>Send test message</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. 501234567"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                />
                <Button
                  variant="outline"
                  onClick={() => testMutation.mutate()}
                  disabled={!testPhone || testMutation.isPending}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="automation">
          <Card className="p-4 space-y-4 max-w-2xl">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-send bill receipt after payment</Label>
                <p className="text-xs text-muted-foreground">Includes receipt link when customer phone is on the bill.</p>
              </div>
              <Switch
                checked={form.auto_send_bill}
                onCheckedChange={(v) => setForm((f) => ({ ...f, auto_send_bill: v }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Order ready (dine-in / takeaway)</Label>
                <p className="text-xs text-muted-foreground">When kitchen marks KOT as Ready.</p>
              </div>
              <Switch
                checked={form.auto_send_order_ready}
                onCheckedChange={(v) => setForm((f) => ({ ...f, auto_send_order_ready: v }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Delivery dispatched + ETA</Label>
                <p className="text-xs text-muted-foreground">When driver is dispatched on delivery board.</p>
              </div>
              <Switch
                checked={form.auto_send_delivery_dispatched}
                onCheckedChange={(v) => setForm((f) => ({ ...f, auto_send_delivery_dispatched: v }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Reservation confirmation</Label>
                <p className="text-xs text-muted-foreground">When a booking is saved in Reservations hub.</p>
              </div>
              <Switch
                checked={form.auto_send_reservation_confirm !== false}
                onCheckedChange={(v) => setForm((f) => ({ ...f, auto_send_reservation_confirm: v }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Waitlist table ready</Label>
                <p className="text-xs text-muted-foreground">When host notifies a walk-in guest.</p>
              </div>
              <Switch
                checked={form.auto_send_waitlist_ready !== false}
                onCheckedChange={(v) => setForm((f) => ({ ...f, auto_send_waitlist_ready: v }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Custom receipt link base (optional)</Label>
              <Input
                placeholder="https://yoursite.com/receipt"
                value={form.receipt_link_base || ""}
                onChange={(e) => setForm((f) => ({ ...f, receipt_link_base: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use built-in secure receipt link (/api/public/receipts/…).
              </p>
            </div>
            <Button onClick={save} disabled={saveMutation.isPending}>Save automation</Button>
          </Card>
        </TabsContent>

        <TabsContent value="reservation">
          <Card className="p-4 space-y-4 max-w-xl">
            <p className="text-sm text-muted-foreground">
              Resend a reservation confirmation manually (bookings are managed in Operations → Reservations).
            </p>
            <div className="grid gap-3">
              <div className="grid gap-2">
                <Label>Guest phone</Label>
                <Input
                  value={reservation.recipient_phone}
                  onChange={(e) => setReservation((r) => ({ ...r, recipient_phone: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label>Guest name</Label>
                <Input
                  value={reservation.guest_name}
                  onChange={(e) => setReservation((r) => ({ ...r, guest_name: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={reservation.reservation_date}
                    onChange={(e) => setReservation((r) => ({ ...r, reservation_date: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={reservation.reservation_time}
                    onChange={(e) => setReservation((r) => ({ ...r, reservation_time: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label>Party size</Label>
                  <Input
                    type="number"
                    min="1"
                    value={reservation.party_size}
                    onChange={(e) => setReservation((r) => ({ ...r, party_size: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Table (optional)</Label>
                  <Input
                    value={reservation.table_ref}
                    onChange={(e) => setReservation((r) => ({ ...r, table_ref: e.target.value }))}
                  />
                </div>
              </div>
              <Button
                onClick={() => reservationMutation.mutate()}
                disabled={reservationMutation.isPending || !reservation.recipient_phone}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Send confirmation
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="logs">
          {logsLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading log…
            </div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No messages logged yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3">When</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-left p-3">Phone</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-t">
                      <td className="p-3 whitespace-nowrap text-xs">
                        {log.sent_at || log.created_at
                          ? new Date(log.sent_at || log.created_at).toLocaleString()
                          : "—"}
                      </td>
                      <td className="p-3">{log.message_type.replace("_", " ")}</td>
                      <td className="p-3 font-mono text-xs">{log.recipient_phone}</td>
                      <td className="p-3">
                        <Pill tone={LOG_STATUS[log.status] || "slate"}>{log.status}</Pill>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground max-w-xs truncate">
                        {log.body_preview || log.error_message || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
